/**
 * Centralized error-code → user-readable message mapping.
 *
 * Backend envelope:
 *   { status: "error", errors: [{ code, module, severity, title, detail, pointer }, ...] }
 *
 * Callers should use `getErrorMessage(err, fallback)` which normalizes both
 * axios-thrown errors (err.response.data.errors) and fetch-thrown errors
 * (err.errors attached by apiFetch).
 */

export interface ApiErrorEntry {
  code: string;
  module?: string;
  severity?: string;
  title?: string;
  detail?: string;
  pointer?: string | null;
}

export interface ErrorMessageEntry {
  message: string;
  guidance?: string;
  category?: string;
}

/**
 * Centralized error message registry with recovery guidance
 * Format: MODULE-XXXX-{TYPE}
 * Types: E (error), W (warning), I (info), S (success)
 */
export const ERROR_MESSAGES: Record<string, ErrorMessageEntry | string> = {
  // ── Identity ────────────────────────────────────────────
  "IDN-0001-E": {
    message: "Authentication required. Your session has expired.",
    guidance: "Please sign in again to continue.",
    category: "authentication",
  },
  "IDN-0003-E": {
    message: "Permission denied. You lack authorization for this action.",
    guidance: "Contact your administrator if you believe you should have access.",
    category: "authorization",
  },
  "IDN-0031-E": {
    message: "Access restricted. You don't have access to this organization.",
    guidance: "Request access from an organization administrator.",
    category: "authorization",
  },
  "IDN-0032-E": {
    message: "Operation failed. Parent organization not found.",
    guidance: "Verify the parent organization exists and try again.",
    category: "organization",
  },
  "IDN-0039-E": {
    message: "Conflict detected. An organization with this name already exists.",
    guidance: "Choose a different name or edit the existing organization.",
    category: "organization",
  },
  "IDN-0063-E": {
    message: "Permission denied. You cannot create organizations.",
    guidance: "Contact your administrator to request organization creation permissions.",
    category: "authorization",
  },
  "IDN-0064-E": {
    message: "Validation failed. Root unit must be a Group or Organization.",
    guidance: "Ensure the root element is correctly typed as a Group or Organization.",
    category: "validation",
  },
  "IDN-0065-E": {
    message: "Constraint violated. Only one root organization is allowed.",
    guidance: "Remove or merge existing organizations before creating a new root.",
    category: "organization",
  },
  "IDN-0066-E": {
    message: "Validation failed. Invalid parent type for this unit.",
    guidance: "Ensure the parent unit type is compatible with the child unit type.",
    category: "validation",
  },
  "IDN-0067-E": {
    message: "Constraint violated. Cannot create a group when an organization already exists.",
    guidance: "Adjust your organizational structure or delete the existing organization.",
    category: "organization",
  },
  "IDN-0069-E": {
    message: "Widget Not Available",
    guidance: "This widget is not enabled for your role. Contact your administrator to have it activated.",
    category: "authorization",
  },

  // ── CMDB ────────────────────────────────────────────────
  "CMDB-0001-E": {
    message: "Not found. The asset does not exist or has been deleted.",
    guidance: "Verify the asset ID and refresh the page.",
    category: "resource",
  },
  "CMDB-0002-E": {
    message: "Not found. The application does not exist or has been deleted.",
    guidance: "Check the application list and select a valid application.",
    category: "resource",
  },
  "CMDB-0003-E": {
    message: "Not found. The component does not exist or has been deleted.",
    guidance: "Verify the component ID and refresh the page.",
    category: "resource",
  },
  "CMDB-0004-E": {
    message: "Not found. The on-premises CI does not exist.",
    guidance: "Check your on-premises configuration or contact your infrastructure team.",
    category: "resource",
  },
  "CMDB-0005-E": {
    message: "Not found. The cloud account does not exist.",
    guidance: "Verify the cloud account configuration in settings.",
    category: "resource",
  },
  "CMDB-0006-E": {
    message: "Not found. The component mapping does not exist.",
    guidance: "Create a new mapping or select an existing one.",
    category: "mapping",
  },
  "CMDB-0007-E": {
    message: "Not found. The asset mapping does not exist.",
    guidance: "Create a new mapping or select an existing one.",
    category: "mapping",
  },
  "CMDB-0008-E": {
    message: "Conflict detected. This component is already mapped to the application.",
    guidance: "Select a different component or update the existing mapping.",
    category: "mapping",
  },
  "CMDB-0009-E": {
    message: "Conflict detected. This asset is already mapped to the application.",
    guidance: "Select a different asset or update the existing mapping.",
    category: "mapping",
  },
  "CMDB-0010-E": {
    message: "Validation failed. Invalid CI reference.",
    guidance: "On-premises assets require an on-premises CI; cloud assets require a cloud account.",
    category: "validation",
  },

  // ── Control Mapping (chain / auto-mapping) ──────────────
  "MAP-0009-E": {
    message: "This collection has no entries yet — cannot derive.",
    guidance: "Add at least one mapping entry to the collection, then try deriving again.",
    category: "mapping",
  },
  "MAP-0010-E": {
    message: "Cannot auto-map: these two collections would create a circular mapping.",
    guidance: "Choose collections that do not map back to each other.",
    category: "mapping",
  },
  "MAP-0011-E": {
    message: "The selected collections do not share a common catalog.",
    guidance: "Auto-mapping needs an intermediary catalog shared by both collections.",
    category: "mapping",
  },
  "MAP-0012-E": {
    message: "No matching controls found between the two collections.",
    guidance: "Ensure the shared catalog controls overlap before deriving.",
    category: "mapping",
  },
  "MAP-0013-E": {
    message: "An auto-derived mapping for these two collections already exists.",
    guidance: "Open the existing derived collection instead of creating a new one.",
    category: "mapping",
  },

  // ── System ──────────────────────────────────────────────
  "SYS-0001-E": {
    message: "Server error. An unexpected error occurred.",
    guidance: "Try again. If the problem persists, contact support.",
    category: "system",
  },
  "SYS-0002-E": {
    message: "Validation failed. Please review the highlighted fields.",
    guidance: "Correct the errors and try again.",
    category: "validation",
  },
  "SYS-0003-E": {
    message: "Bad request. The server could not understand the request.",
    guidance: "Verify your input and try again.",
    category: "system",
  },
  "SYS-0004-E": {
    message: "Not found. The requested resource does not exist.",
    guidance: "Check the URL or navigate back and try again.",
    category: "system",
  },
  "SYS-0005-E": {
    message: "Network error. Connection failed.",
    guidance: "Check your internet connection and try again.",
    category: "network",
  },
};

export function extractApiErrors(err: any): ApiErrorEntry[] {
  if (Array.isArray(err?.errors)) return err.errors;
  if (Array.isArray(err?.response?.data?.errors)) return err.response.data.errors;
  return [];
}

function getMessageFromEntry(entry: ErrorMessageEntry | string): string {
  return typeof entry === "string" ? entry : entry.message;
}

function getGuidanceFromEntry(entry: ErrorMessageEntry | string): string | undefined {
  return typeof entry === "string" ? undefined : entry.guidance;
}

/**
 * Get human-readable error message from error code
 */
export function getErrorMessage(err: any, fallback: string): string {
  const errors = extractApiErrors(err);
  if (errors.length === 0) {
    return err?.response?.data?.message || err?.message || fallback;
  }
  if (errors.length > 1) {
    return errors
      .map((e) => {
        const field = e.pointer?.replace(/^\//, "");
        const entry = ERROR_MESSAGES[e.code];
        const msg = entry ? getMessageFromEntry(entry) : (e.detail || e.title || "Invalid input");
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  const e = errors[0];
  const entry = ERROR_MESSAGES[e.code];
  const message = entry ? getMessageFromEntry(entry) : (e.detail || e.title || fallback);
  return message;
}

/**
 * Get both a title and message from the backend error envelope, for use in
 * toast/alert components that display a heading plus a body.
 * Falls back to the provided defaults when no structured error is present.
 */
export function getErrorTitleAndMessage(
  err: any,
  fallbackTitle: string,
  fallbackMessage: string
): { title: string; message: string } {
  const errors = extractApiErrors(err);
  if (errors.length === 0) {
    return {
      title: fallbackTitle,
      message: err?.response?.data?.message || err?.message || fallbackMessage,
    };
  }
  const e = errors[0];
  const entry = ERROR_MESSAGES[e.code];
  return {
    title: e.title || fallbackTitle,
    message: entry ? getMessageFromEntry(entry) : e.detail || e.title || fallbackMessage,
  };
}

/**
 * Get error message with recovery guidance for better UX
 */
export function getErrorMessageWithGuidance(err: any, fallback: string): { message: string; guidance?: string } {
  const errors = extractApiErrors(err);
  if (errors.length === 0) {
    return { message: err?.response?.data?.message || err?.message || fallback };
  }

  const e = errors[0];
  const entry = ERROR_MESSAGES[e.code];
  const message = entry ? getMessageFromEntry(entry) : (e.detail || e.title || fallback);
  const guidance = entry ? getGuidanceFromEntry(entry) : undefined;

  return { message, guidance };
}

/**
 * Extract field-level errors from API response
 */
export function getFieldErrors(err: any): Record<string, string> {
  const map: Record<string, string> = {};
  for (const e of extractApiErrors(err)) {
    if (!e.pointer) continue;
    const field = e.pointer.replace(/^\//, "");
    const entry = ERROR_MESSAGES[e.code];
    const message = entry ? getMessageFromEntry(entry) : (e.detail || e.title || "Invalid");
    map[field] = message;
  }
  return map;
}
